import { buildReportLink } from '../helpers'
import { AiReportDataType, VerdictData } from '../types'
import { ReportWrapperConstructor } from './report-wrapper-base'

export function ReportMixin<TBase extends ReportWrapperConstructor>(Base: TBase): any {
  return class ReportWithReport extends Base {
    refreshReportCallback: (() => void) | undefined

    /**
     * Get file name by report id
     */
    getFileName(reportId: string) {
      const report = this.getReport(reportId)
      return report?.file?.name ?? ''
    }

    /**
     * Sets a callback which can be called if refreshing the report is needed
     */
    setRefreshReportCallback(callback: () => void) {
      this.refreshReportCallback = callback
    }

    /**
     * Refreshes the report via the callback
     */
    refreshReport() {
      this.refreshReportCallback ? this.refreshReportCallback() : null
    }

    /**
     * Get file hash by report id
     */
    getFileHash(reportId: string) {
      const report = this.getReport(reportId)
      return report?.file.hash
    }

    /**
     * Get file category by report id
     */
    getFileCategory(reportId: string) {
      const report = this.getReport(reportId)
      return report?.file.type
    }

    /**
     * Get current file report id
     */
    getCurrentReportId() {
      const pathname = window.location.pathname
      const match = pathname.match(/\/reports\/([^/]+)\/[^/]+/)

      return match ? match[1] : null
    }

    /**
     * Construct link to go to specific report overview page
     *
     * @param {string} reportId
     * @param {string} subpage
     */
    getReportLink(reportId: string, subpage: string) {
      const flowId = this.getFlowId() as string

      return buildReportLink(flowId, reportId, subpage)
    }

    /**
     * Get final report verdict
     *
     * @param {string} reportId
     */
    getVerdict(reportId: string): VerdictData | undefined {
      const report = this.getReport(reportId)
      return report?.verdict
    }

    /**
     * Get the winning attribution of the report with the report's own tags of the same name
     */
    getHeaderAttribution(reportId: string) {
      const overview = this.getReport(reportId)?.overview
      // The input file comes first in the list, so the first file with an attribution wins
      const file = overview?.threatAttributions?.find((f) => f.threatAttribution?.attributions?.length)

      if (!file) {
        return undefined
      }

      // Ordering is the engine contract: index 0 is always the winning attribution
      const [attribution] = file.threatAttribution.attributions
      // The tags come sorted from the backend, so the first one of a name is the one the tag list shows
      const tags = (overview?.tags ?? []).filter((tag) => !tag.suppressed)
      const tagOf = (name?: string | null) =>
        name ? tags.find((tag) => tag.tag.name.toLowerCase() === name.toLowerCase()) : undefined

      return {
        attribution,
        fileOrigin: file.fileOrigin,
        familyTag: tagOf(attribution.threatName),
        typeTag: tagOf(attribution.threatType),
      }
    }

    /**
     * Get time estimate for given report
     */
    getTimeEstimate(reportId: string): number | undefined {
      const report = this.getReport(reportId)
      return report?.estimatedTime
    }

    /**
     * Get estimated progress for given report
     */
    getEstimatedProgress(reportId: string): number | undefined {
      const report = this.getReport(reportId)
      return report?.overview.estimatedProgress
    }

    getGroupVerdicts(reportId: string): any {
      const report = this.getReport(reportId)
      return report?.groupVerdicts
    }

    /**
     * Get flag indicating that all attempts to load derived files are finished
     */
    getReportFilesLoadingState(reportId: string): boolean | undefined {
      const report = this.getReport(reportId)
      return report?.filesDownloadFinished
    }

    /**
     * Get ChatGPT summary on report, if it already exists in report model
     */
    getChatGptSummary(reportId: string) {
      const report = this.getReport(reportId)
      const aiData = report?.aiData

      for (const item of aiData || []) {
        if (item.data_type === AiReportDataType.Summary) {
          return item
        }
      }

      return undefined
    }

    /**
     * Get scan engine name used to generate the report
     */
    getScanEngine(reportId: string) {
      const report = this.getReport(reportId)
      return report?.scanEngine
    }

    /**
     * Get mdcloud report type
     */
    getMdcloudReportType(reportId: string) {
      const report = this.getReport(reportId)
      return report?.mdcloudReportType
    }

    getUploader(reportId: string) {
      const report = this.getReport(reportId)
      return report?.uploader
    }

    /**
     * Get latest report id for the same input hash
     */
    getLatestReport(reportId: string) {
      const report = this.getReport(reportId)
      return report?.overview.latestReport
    }

    /**
     * Get reduced report flag
     */
    getReduced(reportId: string) {
      const report = this.getReport(reportId)
      return report?.reduced
    }
  }
}
